import { createSelector } from 'reselect';
import { __THOR__, __SERVER_THOR__ } from '@stencil/flights-constants/src/urls';
import getDateObject from '@stencil/flights-date-utils/src/get-date-object';
import { sortListForGroundup, filterFunctionForGroundUp } from '../utils/SRPThorUtils';
import { getFromUrlObjectForGroundUp } from '../../Home/utils/searchDataUtils';
import { flavourSelector } from '../../Home/redux/selectors';

export const srpStateSelector = state => state.srpLite;

export const searchStringSelector = createSelector(srpStateSelector, srp => srp.searchString);

export const urlQueryStringSelector = createSelector(searchStringSelector, searchString => {
  if (!searchString) {
    return null;
  }
  return `air-${searchString.substring(0, searchString.length - 1)}`;
});

export const constructThorUrl = createSelector(
  [urlQueryStringSelector, flavourSelector],
  (searchString, flavour) => {
    if (!searchString) {
      return null;
    }
    const thorUrl = `${__CLIENT__ ? __THOR__ : __SERVER_THOR__}/search/v2?f=${
      flavour === 'desktop' ? 'v2' : 'mobile'
    }&cu=INR&lob=B2C&src=GI&c=false&l=en-IN&stream=${
      __CLIENT__ ? 'true' : 'false'
    }&q=${searchString}`;

    return thorUrl;
  }
);

export const constructPreSearchUrl = createSelector(
  [urlQueryStringSelector, flavourSelector],
  (searchString, flavour) => {
    if (!searchString) {
      return null;
    }
    const preSearchUrl = `${
      __CLIENT__ ? __THOR__ : __SERVER_THOR__
    }/pre-search?q=${searchString}&f=${
      flavour === 'desktop' ? 'v2' : 'mobile'
    }&cu=INR&l=en-IN&lob=B2C&src=GI`;

    return preSearchUrl;
  }
);

export const thorDataSelector = createSelector(srpStateSelector, srp => srp.thorData || {});

export const filtersFromAPISelector = createSelector(thorDataSelector, data => data.filter);

export const quickFilterSelector = createSelector(
  thorDataSelector,
  data => data.quick_filter || {}
);

export const querySelector = createSelector(thorDataSelector, data => data?.query);

export const errorSelector = createSelector(
  thorDataSelector,
  data => data?.err?.error_description || ''
);

export const currentCommonFiltersStateSelector = createSelector(
  srpStateSelector,
  srp => srp.commonFilterSetting
);

export const defaultCommonFiltersSelector = createSelector(
  srpStateSelector,
  srp => srp.defaultCommonFilterSetting
);

export const chunksPrefetchedOnServerSelector = createSelector(
  srpStateSelector,
  srp => srp.isChunksPrefetchedOnServer
);

export const receivedAllChunksSelector = createSelector(
  srpStateSelector,
  srp => srp.receivedAllChunks
);

export const storeStatusSelector = createSelector(srpStateSelector, srp => srp.isStoreEmpty);

export const fareTrendsSelector = createSelector(srpStateSelector, srp => srp.fareTrendsData);

export const fareTrendsPrefetchedOnServerSelector = createSelector(
  srpStateSelector,
  srp => srp.isFareTrendsPrefetchedOnServer
);

export const dsrtSelector = createSelector(thorDataSelector, data => data.dsrt || null);

export const queryParamSelector = createSelector(srpStateSelector, srp => srp.queryParam);

export const homePageDataFromUrlParamsSelector = createSelector(
  [searchStringSelector, querySelector],
  (searchString, queryFromAPI) => {
    const {
      source,
      destination,
      departure,
      arrival,
      passengers,
      flightClass,
      journeyType,
      selectedFareIdentifier
    } = getFromUrlObjectForGroundUp(searchString.substring(0, searchString.length - 1)) || {};
    let newSource = source;
    let newDestination = destination;
    if (queryFromAPI) {
      newSource = { ...source, ...queryFromAPI[source.iata] };
      newDestination = { ...destination, ...queryFromAPI[destination.iata] };
    }
    return {
      source: newSource,
      destination: newDestination,
      departure: getDateObject(departure),
      arrival: getDateObject(arrival),
      passengers,
      flightClass,
      journeyType,
      selectedFareIdentifier
    };
  }
);

export const sourceSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.source
);

export const destinationSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.destination
);

export const departureSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.departure
);

export const travelTypeSelector = createSelector(querySelector, query =>
  query?.isIntl ? 'international' : 'domestic'
);

export const correlationIdSelector = createSelector(querySelector, query => {
  return query?.search_id || null;
});

export const journeyTypeSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.journeyType
);

export const arrivalSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.arrival
);

export const isRoundTripSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.journeyType === 'rt'
);

export const isEconomyClassSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.flightClass?.name === 'Economy' && data?.flightClass?.value === 'E'
);

export const selectedFareIdentifierSelector = createSelector(
  homePageDataFromUrlParamsSelector,
  data => data?.selectedFareIdentifier
);

export const isInternationalTripSelector = createSelector(
  travelTypeSelector,
  travelType => travelType === 'international'
);

export const showFareTrendsSelector = createSelector(
  [isRoundTripSelector, isEconomyClassSelector, selectedFareIdentifierSelector],
  (isRoundTrip, isEconomyClass, selectedFareIdentifier) => {
    return !isRoundTrip && selectedFareIdentifier === 'R' && isEconomyClass;
  }
);

export const showModifySearchSelector = createSelector(
  srpStateSelector,
  srp => srp.showModifySearchWidget
);

export const showOnwardJourneyTypeSelector = createSelector(
  srpStateSelector,
  srp => srp.showOnwardJourneyType
);

export const preSearchBannerDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp?.preSearchBannerDetails?.banners || null
);

export const selectedBannerDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp?.selectedBannerIndex || { bannerPositionIndex: null, horizontalIndex: null }
);

export const selectedPromoCodeSelector = createSelector(
  [selectedBannerDetailsSelector, preSearchBannerDetailsSelector],
  (selectedBannerDetails, bannersData) => {
    if (
      selectedBannerDetails.bannerPositionIndex !== null &&
      selectedBannerDetails.horizontalIndex !== null &&
      Array.isArray(bannersData)
    ) {
      const selectedBanner = bannersData.find(
        banner => banner?.position === selectedBannerDetails.bannerPositionIndex
      );
      const promo_code = selectedBanner?.data?.[selectedBannerDetails.horizontalIndex]?.promo_code;
      if (promo_code) {
        return { promo: promo_code };
      }
    }
    return null;
  }
);

export const bannersPrefetchedOnServerSelector = createSelector(
  srpStateSelector,
  srp => srp.isBannersPrefetchedOnServer
);

export const BRPAppliedOfferPromoCodeSelector = createSelector(
  srpStateSelector,
  srp => srp.brpAppliedOfferPromoCode || ''
);

// #region - ONWARD FLIGHT SELCTORS */
export const defaultOnwardFiltersSelector = createSelector(
  srpStateSelector,
  srp => srp.defaultOnWardFilterSetting
);

export const currentOnwardFiltersStateSelector = createSelector(
  srpStateSelector,
  srp => srp.onwardFilterSetting
);

export const onwardFlightsDataSelector = createSelector(
  thorDataSelector,
  data => data.recommendation?.[0] || []
);

export const sortOnwardFlightSelector = createSelector(
  srpStateSelector,
  srp => srp.sortOnwardFlightsSetting
);

export const filteredOnwardFlightsSelector = createSelector(
  [onwardFlightsDataSelector, currentOnwardFiltersStateSelector, currentCommonFiltersStateSelector],
  (onwardFlights, currentOnwardFilterSettings, currentCommonFilterSettings) => {
    return filterFunctionForGroundUp(onwardFlights, {
      ...currentOnwardFilterSettings,
      ...currentCommonFilterSettings
    });
  }
);

export const onwardSelectedFlightSelector = createSelector(
  srpStateSelector,
  srp => srp.onwardSelectedFlight
);

export const filteredAndSortedDisplayOnwardFlights = createSelector(
  [filteredOnwardFlightsSelector, sortOnwardFlightSelector],
  (onwardFlightData, sortSettings) => {
    return sortListForGroundup([...onwardFlightData], sortSettings);
  }
);

export const filteredSortedOnwardFlightsWithSpecialCardsSelector = createSelector(
  [
    filteredAndSortedDisplayOnwardFlights,
    isRoundTripSelector,
    showFareTrendsSelector,
    flavourSelector,
    preSearchBannerDetailsSelector
  ],
  (list, isRoundTrip, showFareTrends, flavour, bannerDetails) => {
    const clonedList = [...list];
    if (flavour === 'mobile') {
      clonedList.splice(0, 0, { specialCard: 'filters' });

      if (Array.isArray(bannerDetails)) {
        let isSpilledOverBannerIncluded = false;
        bannerDetails.forEach(bannerData => {
          const { type, position } = bannerData;
          if (type?.toLowerCase() === 'inline' && position > 0 && list.length) {
            //only 1 banner can spillover
            if (position > list.length) {
              if (!isSpilledOverBannerIncluded) {
                clonedList.splice(clonedList.length, 0, {
                  specialCard: 'inlineBanner',
                  data: bannerData
                });
                isSpilledOverBannerIncluded = true;
              }
            } else {
              clonedList.splice(position, 0, {
                specialCard: 'inlineBanner',
                data: bannerData
              });
            }
          } else if (type?.toLowerCase() === 'top') {
            clonedList.splice(0, 0, { specialCard: 'specialFaresBanner', data: bannerData });
          }
        });
      }

      if (showFareTrends) {
        clonedList.splice(0, 0, { specialCard: 'fareTrends' });
      }

      if (isRoundTrip) {
        clonedList.splice(0, 0, { specialCard: 'header' }, { specialCard: 'roundTripTabheader' });
      }
    }
    return clonedList;
  }
);

export const onwardScrollLeftValueSelector = createSelector(
  srpStateSelector,
  srp => srp.onwardScrollLeftValue
);
// #endregion

// #region - RETURN FLIGHT SELECTORS */
export const returnFlightsDataSelector = createSelector(
  thorDataSelector,
  data => data.recommendation?.[1] || []
);

export const currentReturnFiltersStateSelector = createSelector(
  srpStateSelector,
  srp => srp.returnFilterSetting
);

export const defaultReturnFiltersSelector = createSelector(
  srpStateSelector,
  srp => srp.defaultReturnFilterSetting
);

export const sortReturnFlightSelector = createSelector(
  srpStateSelector,
  srp => srp.sortReturnFlightsSetting
);

export const filteredReturnFlightsSelector = createSelector(
  [returnFlightsDataSelector, currentReturnFiltersStateSelector, currentCommonFiltersStateSelector],
  (returnFlights, currentReturnFilterSettings, currentCommonFilterSettings) => {
    return filterFunctionForGroundUp(returnFlights, {
      ...currentReturnFilterSettings,
      ...currentCommonFilterSettings
    });
  }
);

export const returnSelectedFlightSelector = createSelector(
  srpStateSelector,
  srp => srp.returnSelectedFlight
);

export const filteredAndSortedDisplayReturnFlights = createSelector(
  [filteredReturnFlightsSelector, sortReturnFlightSelector],
  (reutrnFlightData, sortSettings) => {
    return sortListForGroundup([...reutrnFlightData], sortSettings);
  }
);

export const filteredSortedReturnFlightsWithSpecialCardsSelector = createSelector(
  [filteredAndSortedDisplayReturnFlights, flavourSelector, preSearchBannerDetailsSelector],
  (list, flavour, bannerDetails) => {
    const clonedList = [...list];
    if (flavour === 'mobile') {
      clonedList.splice(0, 0, { specialCard: 'filters' });

      if (Array.isArray(bannerDetails)) {
        let isSpilledOverBannerIncluded = false;
        bannerDetails.forEach(bannerData => {
          const { type, position } = bannerData;
          if (type?.toLowerCase() === 'inline' && position > 0 && list.length) {
            //only 1 banner can spillover
            if (position > list.length) {
              if (!isSpilledOverBannerIncluded) {
                clonedList.splice(clonedList.length, 0, {
                  specialCard: 'inlineBanner',
                  data: bannerData
                });
                isSpilledOverBannerIncluded = true;
              }
            } else {
              clonedList.splice(position, 0, {
                specialCard: 'inlineBanner',
                data: bannerData
              });
            }
          } else if (type?.toLowerCase() === 'top') {
            clonedList.splice(0, 0, { specialCard: 'specialFaresBanner', data: bannerData });
          }
        });
      }

      clonedList.splice(0, 0, { specialCard: 'header' }, { specialCard: 'roundTripTabheader' });
    }
    return clonedList;
  }
);

export const returnScrollLeftValueSelector = createSelector(
  srpStateSelector,
  srp => srp.returnScrollLeftValue
);

// #endregion
